import { toast } from "react-toastify";

import {
  CancelAgenticLogicRuleTestRunResponseBodyType,
  GetAgenticLogicRulePublishJobResponseBodyType,
  PublishAgenticLogicRuleRequestBodyType,
  PublishAgenticLogicRuleRequestPathParamsType,
  PublishAgenticLogicRuleResponseBodyType,
  RunAgenticLogicRuleGatesRequestBodyType,
  RunAgenticLogicRuleGatesRequestPathParamsType,
  RunAgenticLogicRuleGatesResponseBodyType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

const publishAgenticLogicRule = async (
  payload: ApiRequest<
    PublishAgenticLogicRuleRequestBodyType,
    PublishAgenticLogicRuleRequestPathParamsType
  >
) => {
  const resp = await api.post<
    PublishAgenticLogicRuleResponseBodyType,
    PublishAgenticLogicRuleRequestBodyType
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/publish`,
    payload.body
  );
  return resp.data;
};

/**
 * Enqueues a publish job and answers only `{ publishJobId }`. Progress is
 * polled — see `useAgenticLogicPublishProgress`.
 */
export const usePublishAgenticLogicRule = () => {
  return useMutation<
    PublishAgenticLogicRuleResponseBodyType,
    Error,
    ApiRequest<
      PublishAgenticLogicRuleRequestBodyType,
      PublishAgenticLogicRuleRequestPathParamsType
    >,
    unknown
  >({
    mutationFn: async (data) => publishAgenticLogicRule(data),
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(`Failed to publish${errorDetail ? `. ${errorDetail}` : ""}`);
    },
  });
};

const runAgenticLogicRuleGates = async (
  payload: ApiRequest<
    RunAgenticLogicRuleGatesRequestBodyType,
    RunAgenticLogicRuleGatesRequestPathParamsType
  >
) => {
  const resp = await api.post<
    RunAgenticLogicRuleGatesResponseBodyType,
    RunAgenticLogicRuleGatesRequestBodyType
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/run-gates`,
    payload.body
  );
  return resp.data;
};

/**
 * Enqueues a dry-run job that evaluates the publish gates without promoting a
 * version, and answers only `{ publishJobId }`. Progress polls through the same
 * `useAgenticLogicPublishProgress` path as a real publish — the job id is the
 * only handle either flow needs.
 */
export const useRunAgenticLogicRuleGates = () => {
  return useMutation<
    RunAgenticLogicRuleGatesResponseBodyType,
    Error,
    ApiRequest<
      RunAgenticLogicRuleGatesRequestBodyType,
      RunAgenticLogicRuleGatesRequestPathParamsType
    >,
    unknown
  >({
    mutationFn: async (data) => runAgenticLogicRuleGates(data),
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to run tests${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};

export const useCancelAgenticLogicRuleTestRun = () => {
  const queryClient = useQueryClient();
  return useMutation<
    CancelAgenticLogicRuleTestRunResponseBodyType,
    Error,
    { ruleId: string; jobId: string }
  >({
    mutationFn: async ({ ruleId, jobId }) => {
      const response = await api.post<
        CancelAgenticLogicRuleTestRunResponseBodyType,
        { jobId: string }
      >(`/v2/agentic-logic/rules/${ruleId}/cancel-test-run`, { jobId });
      return response.data;
    },
    onSuccess: ({ job }, { ruleId, jobId }) => {
      queryClient.setQueryData(agenticLogicKeys.publishJob(ruleId, jobId), {
        job,
      });
      void queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rule(ruleId),
      });
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to stop tests${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};

const getAgenticLogicRulePublishJob = async (
  ruleId: string,
  jobId: string
): Promise<GetAgenticLogicRulePublishJobResponseBodyType> => {
  const resp = await api.get<GetAgenticLogicRulePublishJobResponseBodyType>(
    `/v2/agentic-logic/rules/${ruleId}/publish-jobs/${jobId}`
  );
  return resp.data;
};

/**
 * Polls a publish job while it is `queued` or `running`, and stops on a
 * terminal status. There is no Agentic Logic websocket topic, so unlike the
 * shipping-provider equivalent this is the live progress source rather than a
 * one-shot snapshot read.
 */
export const useAgenticLogicRulePublishJob = (
  ruleId: string | undefined,
  jobId: string | undefined,
  options?: { enabled?: boolean }
) => {
  return useQuery<GetAgenticLogicRulePublishJobResponseBodyType, Error>({
    queryKey: agenticLogicKeys.publishJob(ruleId!, jobId!),
    queryFn: async () => getAgenticLogicRulePublishJob(ruleId!, jobId!),
    refetchInterval: (query) => {
      const status = query.state.data?.job.status;

      return status === "queued" || status === "running" ? 2_000 : false;
    },
    enabled: !!ruleId && !!jobId && options?.enabled !== false,
  });
};
